import {
    RefreshTokenRequest,
    RefreshTokenResponse,
    RegisterTokenRequest,
    TokenRequest,
    TokenResponse,
    ProviderTokenRequest,
    AuthenticationMessageKey,
    ValidateTwoFactAuthCodeRequest,
    UserPasswordPolicy
} from '../../models/authentication/token.models';
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { autoinject } from "aurelia-framework";
import {Urls} from '../../constants/authentication/token.constants';
import { CacheClientService, CacheLocation } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { UserClientService } from './user-client.service';
import { FacebookProvider } from './facebook-provider';
import { GoogleProvider } from './google-provider';
import { Auth0Provider } from './auth0-provider';
import { IBaseAuthProvider } from './base-auth-provider';
import { ConfigService } from '@rightship-tech/common-ui/services/basic/config.service';
import { UrlHelper } from '../../helpers/url-helper';

@autoinject
export class TokenClient {

    constructor(private requestClient: RequestClientService,
        private userClientService: UserClientService,
        private facebookProvider: FacebookProvider,
        private googleProvider: GoogleProvider,
        private auth0Provider: Auth0Provider,
        private cacheClient: CacheClientService,
        private configService: ConfigService) { }

    load() {
        return new Promise<void>((resolve, reject) => {
            const promiseOne = this.facebookProvider.loadProvider();
            const promiseTwo = this.googleProvider.loadProvider();
            Promise.all([promiseOne, promiseTwo]).then(() => resolve()).catch(() => reject());
        });
    }

    getLoginProvider(providerName:string){
        let provider: IBaseAuthProvider = null;
        if (providerName == "google") {
            provider = this.googleProvider;
        } else if (providerName == "facebook") {
            provider = this.facebookProvider;
        } else if (providerName == "auth0") {
            provider = this.auth0Provider;
        }
        return provider;
    }

    loginWithProvider(providerName: string, callback: (item: ProviderTokenRequest) => Promise<any> = null, createIfNotExist: boolean): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const provider: IBaseAuthProvider = this.getLoginProvider(providerName);
            provider.getAuthToken(response => {
                if(response){
                    const userId = response.userId;
                    const accessToken = response.accessToken;
                    const subProviderName = response.subProviderName;
                    const metadata = response.metadata;
                    provider.fetchUserDetails().then(resp => {
                        if (!callback) {
                            callback = this.loginWithThirdPartyProvider;
                        }
                        callback(
                            {
                                email: resp.email,
                                firstName: resp.firstName,
                                lastName: resp.lastName,
                                providerKey: userId,
                                providerName: providerName,
                                providerAccessToken: accessToken,
                                subProviderName: subProviderName,
                                metadata: metadata,
                                createIfNotExist: createIfNotExist,
                                doNotSaveUserLogin: response.doNotSaveUserLogin,
                                campaign: UrlHelper.getParameterValueByKey('utm_campaign', location.href)
                            }).then(() => {
                                resolve();
                            }).catch(response => {
                                const messages = JSON.parse(response.response).messages;
                                this.cacheErrorMessages(messages);
                                provider.logout(accessToken).then(() => {
                                    reject(response);
                                });
                            });
              
                    });
                }
            });
        });
    }

    register(tokenRequest: RegisterTokenRequest): Promise<TokenResponse> {
        return new Promise<TokenResponse>((resolve, reject) => {
            this.requestClient.post<TokenResponse>(Urls.Register, {}, tokenRequest).then(success => {
                resolve(success);
            }, error => {
                const messages = JSON.parse(error.response).messages;
                this.cacheErrorMessages(messages);
                reject(error);
            });
        });
    }

    login(tokenRequest: TokenRequest): Promise<TokenResponse> {
        return new Promise<TokenResponse>((resolve, reject) => {
            this.requestClient.post<TokenResponse>(Urls.Login, {}, tokenRequest).then(success => {
                resolve(success);
            }, error => {
                const messages = JSON.parse(error.response).messages;
                this.cacheErrorMessages(messages);
                reject(error);
            });
        });
    }

    changePassword(request: any): Promise<any> {
        return this.requestClient.post(Urls.ChangePassword, {}, request);
    }

    getPasswordPolicy(): Promise<UserPasswordPolicy> {
        return this.requestClient.get(Urls.GetPasswordPolicy, {});
    }

    validateTwoFactAuthCode(code: ValidateTwoFactAuthCodeRequest): Promise<TokenResponse> {
        return new Promise<TokenResponse>((resolve, reject) => {
            this.requestClient.post<TokenResponse>(Urls.ValidateTwoFactAuthCode, {}, code).then(success => {
                resolve(success);
            }, error => {
                reject(error);
            });
        });
    }

    resendCode(): Promise<void> {
        return new Promise<void>((resolve) => {
            this.requestClient.post<void>(Urls.ResendCode, {}).then(success => {
                resolve(success);
            },  () => {

            });
        });
    }

    loginWithThirdPartyProvider = (request: ProviderTokenRequest): Promise<TokenResponse> => {
        return new Promise<TokenResponse>((resolve, reject) => {
            this.requestClient.post<TokenResponse>(Urls.LoginWithThirdPartyProvider, {}, request).then(success => {
                resolve(success);
            }, error => {
                const messages = JSON.parse(error.response).messages;
                this.cacheErrorMessages(messages);
                reject(error);
            });
        });
    }

    logout(keepContext:boolean = true, redirectToLogin:boolean = true): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.requestClient.post<any>(Urls.Logout, {}).then(({ providerName, providerAccessToken }) => {
                const provider: IBaseAuthProvider = this.getLoginProvider(providerName);
                provider.logout(providerAccessToken).then(() => {
                    if (redirectToLogin) {
                        this.redirectToLogin(keepContext);
                    }

                    this.userClientService.currentUser = null;
                    resolve();
                }, reject);
            },  () => {
                 if (redirectToLogin) {
                    this.redirectToLogin(keepContext);
                 }
                this.userClientService.currentUser = null;
                reject();
            });
        });
    }

    redirectToLogin(keepContext: boolean = true) {
        if (location.href.indexOf(this.configService.config.common.auth0Settings.loginPath || '/login') == -1) {
            const referer = keepContext? `?referrer=${window.location.href}`: "";
            location.href = location.origin + (this.configService.config.common.auth0Settings.loginPath || '/login') + referer;
        }
    }

    refreshToken(tokenRequest: RefreshTokenRequest) {
        return this.requestClient.post<RefreshTokenResponse>(Urls.RefreshRelativePath, {}, tokenRequest);
    }

    private cacheErrorMessages(messages: string[]) {
        if (messages) {
            this.cacheClient.put(AuthenticationMessageKey, messages.join("\n"), CacheLocation.SessionStorage);
        }
    }
}
